<script>
  import { buildSizedImageUrl } from '@/lib/vue-helpers.js';

  export default {
    name: 'CompareProductsWidget',
    props: {
      productsToCompare: Set,
      collectionHandle: String,
      collectionTitle: String
    },
    emits: ['update:productsToCompare', 'keep-open'],
    data() {
      return {
        compareButtonLabel: null,
        bodyClassAdded: false,
        imageHeightDesktop: null,
        imageHeightMobile: null,
        widgetBg: null,
        buttonBg: null,
        buttonText: null,
        widgetTextColor: null,
        widgetProductBg: null,
        aspectRatio: null,
        imageFit: null,
        settingsRetryCount: 0,
        settingsElement: null,
        viewportClass: '',
        _productsCache: null,
        _productsCacheKey: null,
        _urlCache: null,
        _urlCacheKey: null
      };
    },
    computed: {
      products() {
        const cacheKey = this.productsToCompare?.size || 0;
        if (this._productsCache && this._productsCacheKey === cacheKey) {
          return this._productsCache;
        }
        const products = Array.from(this.productsToCompare || new Set()).slice(0, 3);
        this._productsCache = products;
        this._productsCacheKey = cacheKey;
        return products;
      },
      cardWidthDesktop() {
        return this.calculateCardWidth(this.imageHeightDesktop);
      },
      cardWidthMobile() {
        return this.calculateCardWidth(this.imageHeightMobile);
      },
      popupMaxWidthDesktop() {
        if (!this.cardWidthDesktop) return null;
        const cardGap = 32;
        const contentGap = 16;
        const actionsWidth = 240;
        const cardsWidth = 3 * this.cardWidthDesktop + 2 * cardGap;
        return cardsWidth + contentGap + actionsWidth;
      },
      popupMinHeightDesktop() {
        if (!this.imageHeightDesktop) return null;
        const productNameHeight = 40;
        const padding = 32;
        return this.imageHeightDesktop + productNameHeight + padding;
      },
      popupMaxWidthMobile() {
        if (!this.cardWidthMobile) return null;
        const cardGap = 16;
        const sidePadding = 24;
        const cardsWidth = 3 * this.cardWidthMobile + 2 * cardGap;
        return cardsWidth + sidePadding;
      },
      popupMinHeightMobile() {
        if (!this.imageHeightMobile) return null;
        const productNameHeight = 30;
        const actionsHeight = 80;
        const padding = 24;
        return this.imageHeightMobile + productNameHeight + actionsHeight + padding;
      },
      themeStyleVars() {
        const vars = {};
        if (this.imageHeightDesktop) {
          const h = this.imageHeightDesktop;
          vars['--cpw-image-height-desktop'] = `${h}px`;
          vars['--cpw-product-name-margin-top-desktop'] = '0';
          vars['--cpw-card-min-height-desktop'] = `${Math.round(h * 1.154)}px`;
          vars['--cpw-desktop-padding-top'] = '32px';
          vars['--cpw-desktop-padding-bottom'] = '32px';
        }
        if (this.imageHeightMobile) {
          const h = this.imageHeightMobile;
          vars['--cpw-image-height-mobile'] = `${h}px`;
          vars['--cpw-product-name-margin-top-mobile'] = '0';
          vars['--cpw-mobile-padding-top'] = '20px';
          vars['--cpw-mobile-padding-bottom'] = '20px';
          vars['--cpw-mobile-list-padding-top'] = '20px';
        }
        if (this.cardWidthDesktop) vars['--cpw-card-width-desktop'] = `${this.cardWidthDesktop}px`;
        if (this.cardWidthMobile) vars['--cpw-card-width-mobile'] = `${this.cardWidthMobile}px`;
        if (this.popupMaxWidthDesktop) vars['--cpw-popup-max-width-desktop'] = `${this.popupMaxWidthDesktop}px`;
        if (this.popupMinHeightDesktop) vars['--cpw-popup-min-height-desktop'] = `${this.popupMinHeightDesktop}px`;
        if (this.popupMaxWidthMobile) vars['--cpw-popup-max-width-mobile'] = `${this.popupMaxWidthMobile}px`;
        if (this.popupMinHeightMobile) vars['--cpw-popup-min-height-mobile'] = `${this.popupMinHeightMobile}px`;
        if (this.widgetBg) vars['--cpw-widget-bg'] = this.widgetBg;
        if (this.buttonBg) vars['--cpw-button-bg'] = this.buttonBg;
        if (this.buttonText) vars['--cpw-button-text'] = this.buttonText;
        if (this.widgetTextColor) vars['--cpw-widget-text'] = this.widgetTextColor;
        if (this.widgetProductBg) vars['--cpw-widget-product-bg'] = this.widgetProductBg;
        if (this.imageFit) vars['--cpw-image-fit'] = this.imageFit;
        return vars;
      },
      compareProductsUrl() {
        const products = this.products;
        const cacheKey = `${products.length}-${this.collectionHandle || ''}-${this.collectionTitle || ''}`;
        if (this._urlCache && this._urlCacheKey === cacheKey) {
          return this._urlCache;
        }
        const params = new URLSearchParams();
        const ids = products.map(p => p.pid).filter(Boolean);
        if (ids.length) {
          params.set('products', ids[0]);
          for (let i = 1; i < ids.length; i++) {
            params.append('product', ids[i]);
          }
        }
        if (this.collectionHandle) params.set('collection_handle', this.collectionHandle);
        if (this.collectionTitle) params.set('collection_title', this.collectionTitle);
        const url = `/pages/compare-products?${params.toString()}`;
        this._urlCache = url;
        this._urlCacheKey = cacheKey;
        return url;
      },
      isCompareActive() {
        return this.products.length >= 2;
      },
      placeholderCount() {
        return 3 - this.products.length;
      }
    },
    watch: {
      productsToCompare: {
        handler(newVal, oldVal) {
          const newSize = newVal?.size || 0;
          const oldSize = oldVal?.size || 0;
          if (newSize !== oldSize) {
            this._productsCache = null;
            this._productsCacheKey = null;
            this._urlCache = null;
            this._urlCacheKey = null;
          }
          this.toggleCompareOpenClass();
        },
        deep: false
      }
    },
    mounted() {
      this.updateViewportClass();
      document.addEventListener('theme:resize:width', this.updateViewportClass);
      this.loadSettings();
      this.toggleCompareOpenClass();
    },
    beforeUnmount() {
      document.removeEventListener('theme:resize:width', this.updateViewportClass);
      this.removeBodyClass();
      if (this.settingsRetryTimeout) {
        clearTimeout(this.settingsRetryTimeout);
        this.settingsRetryTimeout = null;
      }
    },
    methods: {
      updateViewportClass() {
        const width = window.innerWidth;
        const mobileMax = window.theme?.sizes?.medium;
        const tabletMax = window.theme?.sizes?.large;
        let viewportClass = 'is-desktop';
        if (width <= mobileMax) {
          viewportClass = 'is-mobile';
        } else if (width <= tabletMax) {
          viewportClass = 'is-tablet';
        }
        this.viewportClass = viewportClass;
      },
      loadSettings() {
        if (this.settingsElement) {
          this.processSettings(this.settingsElement);
          return;
        }
        const el = document.getElementById('fs-collection-products');
        if (!el) {
          if (this.settingsRetryCount < 50) {
            this.settingsRetryCount++;
            this.settingsRetryTimeout = setTimeout(() => this.loadSettings(), 100);
          }
          return;
        }
        this.settingsElement = el;
        this.settingsRetryCount = 0;
        this.processSettings(el);
      },
      processSettings(el) {
        const getAttr = name => el.getAttribute(name);
        const settings = {
          compareButtonLabel: getAttr('data-plp-compare-button-label'),
          imageHeightDesktop: getAttr('data-plp-compare-widget-image-height-desktop'),
          imageHeightMobile: getAttr('data-plp-compare-widget-image-height-mobile'),
          widgetBg: getAttr('data-plp-compare-widget-bg'),
          buttonBg: getAttr('data-plp-compare-widget-button-bg'),
          buttonText: getAttr('data-plp-compare-widget-button-text'),
          widgetTextColor: getAttr('data-plp-compare-widget-text'),
          widgetProductBg: getAttr('data-plp-compare-widget-bg-product'),
          productGridImage: getAttr('data-product-grid-image')
        };
        if (settings.compareButtonLabel) this.compareButtonLabel = settings.compareButtonLabel;
        const desktopH = Number(settings.imageHeightDesktop);
        const mobileH = Number(settings.imageHeightMobile);
        if (desktopH) this.imageHeightDesktop = desktopH;
        if (mobileH) this.imageHeightMobile = mobileH;
        if (settings.widgetBg) this.widgetBg = settings.widgetBg;
        if (settings.buttonBg) this.buttonBg = settings.buttonBg;
        if (settings.buttonText) this.buttonText = settings.buttonText;
        const textColor = settings.widgetTextColor?.trim();
        if (textColor) this.widgetTextColor = textColor;
        const productBg = settings.widgetProductBg?.trim();
        if (productBg) this.widgetProductBg = productBg;
        const gridImageSetting = settings.productGridImage?.trim();
        this.imageFit = gridImageSetting === 'scale' ? 'contain' : 'cover';
        this.loadAspectRatio();
      },
      loadAspectRatio() {
        const ratioStr = getComputedStyle(document.documentElement).getPropertyValue('--PRODUCT-GRID-ASPECT-RATIO-DECIMAL').trim();
        if (!ratioStr) return;
        this.aspectRatio = ratioStr.includes('/') ? ratioStr.split('/').map(s => parseFloat(s.trim())) : [parseFloat(ratioStr), 1];
      },
      calculateCardWidth(imageHeight) {
        if (!imageHeight || imageHeight <= 0 || !this.aspectRatio) return null;
        const [width, height] = this.aspectRatio;
        if (!width || !height) return null;
        return Math.round(imageHeight * (width / height)) + 2;
      },
      toggleCompareOpenClass() {
        if (this.bodyClassAdded) return;
        document.body.classList.add('compare-open');
        this.bodyClassAdded = true;
      },
      removeBodyClass() {
        if (this.bodyClassAdded) {
          document.body.classList.remove('compare-open');
          this.bodyClassAdded = false;
        }
      },
      clearAll() {
        this.$emit('keep-open', true);
        this.$emit('update:productsToCompare', new Set());
      },
      close() {
        this.$emit('keep-open', false);
        this.$emit('update:productsToCompare', new Set());
      },
      getProductImageUrl(product) {
        const raw = product.compareImage || product.images?.[0]?.node?.url || product.images?.[0] || product.image || product.t || '';
        return buildSizedImageUrl(raw, 500);
      },
      getProductPdpUrl(product) {
        const handle = product.handle || product.u?.replace('/products/', '') || '';
        if (!handle) return '#';
        const root = window.theme?.routes?.root_url || '/';
        const base = root.endsWith('/') ? root : `${root}/`;
        return this.collectionHandle ? `${base}collections/${this.collectionHandle}/products/${handle}` : `${base}products/${handle}`;
      },
      removeProduct(pid) {
        if (!this.productsToCompare || !this.productsToCompare.size) return;
        const next = new Set();
        for (const item of this.productsToCompare) {
          const id = item?.pid || item?.id;
          if (id && id !== pid) {
            next.add(item);
          }
        }
        if (!next.size) {
          this.close();
          return;
        }
        this.$emit('update:productsToCompare', next);
      }
    }
  };
</script>

<template>
  <Teleport to="#theme-overlays">
    <section class="compare-products" :class="viewportClass" :style="themeStyleVars">
      <div class="compare-products__content">
        <ul class="compare-products__list">
          <li v-for="product in products" :key="product.pid" class="filled">
            <div class="compare-products__image">
              <img :src="getProductImageUrl(product)" :alt="product.title || product.productTitle || product.l || product.name" loading="eager" @load="$event.target.classList.add('is-loaded')" />
            </div>
            <div class="compare-products__product-info">
              <a :href="getProductPdpUrl(product)" class="compare-products__product-name gt-p__small">
                {{ product.title || product.productTitle || product.l || product.name }}
              </a>
              <button type="button" class="compare-products__remove-product" aria-label="Remove product" @click="removeProduct(product.pid)">
                <svg v-once width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.3977 0.397461L0.397705 12.3975M0.397705 0.397461L12.3977 12.3975" stroke="currentColor" stroke-width="1.125" stroke-miterlimit="10" />
                </svg>
              </button>
            </div>
          </li>
          <li v-for="index in placeholderCount" :key="`placeholder-${index}`" class="placeholder">
            <p v-if="products.length < 2 && index === 1" class="compare-products__help gt-p__small">Select at least two items to compare</p>
          </li>
        </ul>
        <ul class="compare-products__actions">
          <li>
            <a v-if="isCompareActive" :href="compareProductsUrl" class="compare-products__compare-items gt-p__small is-active">
              {{ compareButtonLabel }}
            </a>
            <span v-else class="compare-products__compare-items gt-p__small" :class="{ 'is-disabled': !isCompareActive }">
              {{ compareButtonLabel }}
            </span>
          </li>
          <li>
            <button type="button" class="compare-products__clear-all gt-p__small" @click="clearAll" v-once>CLEAR ALL</button>
          </li>
        </ul>
      </div>
      <button type="button" class="compare-products__close" aria-label="Close" @click="close">
        <svg v-once width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.3977 0.397461L0.397705 12.3975M0.397705 0.397461L12.3977 12.3975" stroke="currentColor" stroke-width="1.125" stroke-miterlimit="10" />
        </svg>
      </button>
    </section>
  </Teleport>
</template>

<style lang="scss">
  .compare-products {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    bottom: 16px;
    z-index: var(--z-filter-bar);
    width: calc(100% - 2rem);
    max-width: var(--cpw-popup-max-width-desktop);
    min-height: var(--cpw-popup-min-height-desktop);
    background-color: color-mix(in srgb, var(--cpw-widget-bg) 80%, transparent);
    border: 1px solid;
    border-image-source: linear-gradient(180deg, #ffffff 0%, #808080 36%, #ffffff 100%);
    box-shadow: 0px 2px 15px 0px rgba(0, 0, 0, 0.25);
    backdrop-filter: blur(2px);
    padding-left: 1rem;
    padding-right: 1rem;
    padding-top: var(--cpw-desktop-padding-top, 1rem);
    padding-bottom: var(--cpw-desktop-padding-bottom, 1rem);
    border-radius: 16px;
    margin-bottom: 1rem;
    contain: layout style paint;

    @at-root {
      .compare-open #gorgias-chat-container,
      .compare-open .kl-hub-launcher,
      .compare-open [data-floating-ui-portal],
      body:has(.compare-products) #gorgias-chat-container,
      body:has(.compare-products) .kl-hub-launcher,
      body:has(.compare-products) [data-floating-ui-portal] {
        display: none !important;
      }
    }

    .compare-products__content {
      display: flex;
      flex-direction: row;
      gap: 1rem;

      .compare-products__list {
        display: flex;
        flex-direction: row;
        gap: 1rem;
        justify-content: center;

        li {
          position: relative;
          box-sizing: border-box;
          width: var(--cpw-card-width-desktop);
          min-height: var(--cpw-card-min-height-desktop);
          flex: 0 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          list-style: none;
          text-align: center;

          &.placeholder {
            border: 1px dashed var(--cpw-widget-text);
            justify-content: center;
          }

          &.filled {
            border: 1px solid var(--cpw-widget-text);
          }

          .compare-products__help {
            width: 80%;
            margin: 0 auto;
            text-align: center;
            color: var(--cpw-widget-text);
          }

          .compare-products__image {
            width: 100%;
            display: block;
            align-self: stretch;
            max-height: var(--cpw-image-height-desktop);
            aspect-ratio: var(--PRODUCT-GRID-ASPECT-RATIO-DECIMAL);
            overflow: hidden;
            contain: layout;
            background-color: transparent;

            img {
              display: block;
              width: 100%;
              height: 100%;
              object-fit: var(--cpw-image-fit);
              opacity: 0;
              transition: opacity 0.15s ease;

              &.is-loaded {
                opacity: 1;
              }
            }
          }

          .compare-products__product-name {
            margin: 0;
            width: 100%;
            padding-left: 5%;
            padding-right: 5%;
            text-align: left;
            color: var(--cpw-widget-text);
            text-decoration: none;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .compare-products__remove-product {
            position: absolute;
            right: 0.1rem;
            top: 0.1rem;
            background: transparent;
            border: none;
            padding: 4px;
            cursor: pointer;
            color: var(--cpw-widget-text);
            z-index: 10;
            display: flex;
            align-items: center;
            justify-content: center;

            svg {
              height: 10px;
              width: 10px;
            }

            &:hover {
              opacity: 0.7;
            }
          }
        }

        .compare-products__product-info {
          background-color: var(--cpw-widget-product-bg);
          width: 100%;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-self: stretch;
          padding-top: 3%;
          padding-bottom: 4%;
          overflow: hidden;
        }
      }
    }

    .compare-products__actions {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 1rem;
      align-items: center;
      justify-content: center;
      text-align: center;

      li {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .compare-products__compare-items,
      .compare-products__clear-all {
        font: inherit;
        font-size: var(--GT-P-FONT-SIZE-DESK);
        line-height: inherit;
      }

      .compare-products__compare-items {
        padding: 24px 60px;
        background-color: var(--cpw-button-bg);
        color: var(--cpw-button-text);
        text-transform: uppercase;
        text-decoration: none;
        text-align: center;
        display: inline-block;
        white-space: nowrap;

        &.is-disabled {
          opacity: 0.5;
          cursor: not-allowed;
          pointer-events: none;
        }
      }

      .compare-products__clear-all {
        background: transparent;
        border: none;
        padding: 0;
        cursor: pointer;
        text-decoration: underline;
        color: var(--cpw-widget-text);
      }
    }

    .compare-products__close {
      position: absolute;
      top: 12px;
      right: 12px;
      background-color: transparent;
      border: none;
      padding: 0;
      cursor: pointer;
      color: var(--cpw-widget-text);

      svg {
        height: 12px;
        width: 12px;
      }
    }

    &.is-mobile {
      box-sizing: border-box;
      bottom: 6px;
      margin-bottom: 0;
      width: calc(100% - 1.5rem);
      max-width: var(--cpw-popup-max-width-mobile, 100%);
      min-height: var(--cpw-popup-min-height-mobile);
      padding-left: 0.75rem;
      padding-right: 0.75rem;
      padding-top: var(--cpw-mobile-padding-top);
      padding-bottom: var(--cpw-mobile-padding-bottom);

      .compare-products__content {
        flex-direction: column;
        width: 100%;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
        overflow: hidden;
      }

      .compare-products__actions {
        flex-direction: row-reverse;
        justify-content: center;
        width: 100%;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
        margin: 0;

        li {
          width: auto;
          min-width: 0;
          max-width: 100%;
          box-sizing: border-box;

          &:first-child {
            flex: 2;
          }

          &:last-child {
            flex: 1;
          }
        }

        .compare-products__compare-items,
        .compare-products__clear-all {
          font-size: var(--GT-P-FONT-SIZE-MOBL);
        }

        .compare-products__compare-items {
          width: 100%;
          min-width: 0;
          max-width: 100%;
          height: 48px;
          min-height: 48px;
          padding: 0 1rem;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          border: none;
          line-height: 1;
          white-space: nowrap;

          &.is-disabled {
            opacity: 0.5;
            cursor: not-allowed;
            pointer-events: none;
          }
        }

        .compare-products__clear-all {
          white-space: nowrap;
        }
      }

      .compare-products__list {
        width: 100%;
        max-width: 100%;
        min-width: 0;
        margin: 0;
        padding-top: var(--cpw-mobile-list-padding-top, 1.5rem);
      }

      .compare-products__list li {
        flex: 1 1 0;
        min-width: 0;
        width: auto;
        max-width: var(--cpw-card-width-mobile);
        min-height: auto;
        align-items: flex-start;
        gap: 0;
        margin: 0;

        .compare-products__image {
          flex: 0 0 auto;
          max-height: var(--cpw-image-height-mobile);
          aspect-ratio: var(--PRODUCT-GRID-ASPECT-RATIO-DECIMAL);
          align-self: stretch;
          overflow: hidden;
          contain: layout;

          img {
            width: 100%;
            height: 100%;
            object-fit: var(--cpw-image-fit);
          }
        }

        .compare-products__product-name {
          margin: 0;
          flex: 0 0 auto;
          text-align: left;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }
</style>
